import React, { useState } from 'react';
import {
    Table, Button, Space, Typography, Tag, message, Modal, Form,
    Input, Select, Drawer, Alert, Breadcrumb, Card, Tree, Tooltip
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled, ArrowLeftOutlined } from '@ant-design/icons';
import { getEnabledPrimaryPlatforms } from '../../utils/adPlatforms';

const { Title, Text } = Typography;

// 广告类型枚举
const AD_TYPE_MAP = {
    splash: { label: '开屏', color: 'blue' },
    interstitial: { label: '插屏', color: 'purple' },
    banner: { label: 'Banner', color: 'green' },
    native: { label: '原生', color: 'orange' },
    rewarded: { label: '激励视频', color: 'red' },
};
const AD_TYPE_OPTIONS = Object.keys(AD_TYPE_MAP).map((k) => ({ value: k, label: AD_TYPE_MAP[k].label }));

// 广告平台下拉：读取「广告平台配置」字典中启用的一级 code
const PLATFORM_OPTIONS = getEnabledPrimaryPlatforms();

// TODO: 联调时替换为接口数据 — 应用（一级，key 即应用内部ID），其下为「系统平台-区域」（二级）；广告源关联App使用
const MOCK_APP_TREE = [
    {
        key: 'app_001', title: 'test-HomeStar',
        children: [
            { key: 'app_001__iOS-中国大陆', title: 'iOS-中国大陆' },
            { key: 'app_001__iOS-海外', title: 'iOS-海外' },
            { key: 'app_001__Android-中国大陆', title: 'Android-中国大陆' },
            { key: 'app_001__Android-全球', title: 'Android-全球' },
        ],
    },
    {
        key: 'app_002', title: 'test-BabyCare',
        children: [
            { key: 'app_002__iOS-中国大陆', title: 'iOS-中国大陆' },
            { key: 'app_002__Android-全球', title: 'Android-全球' },
        ],
    },
    {
        key: 'app_003', title: 'test-SmartCam Pro',
        children: [
            { key: 'app_003__iOS-海外', title: 'iOS-海外' },
            { key: 'app_003__Android-海外', title: 'Android-海外' },
        ],
    },
];

// 全部树节点 key（默认全选用）
const ALL_APP_KEYS = [];
const collectAppKeys = (nodes) => {
    nodes.forEach((n) => {
        ALL_APP_KEYS.push(n.key);
        if (n.children) collectAppKeys(n.children);
    });
};
collectAppKeys(MOCK_APP_TREE);

// 由应用树节点 key 反查标题
const getAppKeyTitle = (key) => {
    const findTitle = (nodes) => {
        for (let i = 0; i < nodes.length; i += 1) {
            if (nodes[i].key === key) return nodes[i].title;
            if (nodes[i].children) {
                const t = findTitle(nodes[i].children);
                if (t) return t;
            }
        }
        return null;
    };
    return findTitle(MOCK_APP_TREE) || key;
};

// TODO: 联调时替换为接口数据 — 全局广告位库（与 App 无关联）
const MOCK_GLOBAL_SLOTS = [
    {
        key: '1', name: 'test-开屏广告位', slotId: 'test-gslot-001', type: 'splash', createTime: '2026-08-01 10:00:00', refCount: 3,
        sources: [
            { key: 's1', title: 'test-开屏广告-主源', adId: 'S47382', platform: 'fengchuan', sourceId: 'test-fc-src-001', refCount: 3, appKeys: [...ALL_APP_KEYS] },
            { key: 's2', title: 'test-开屏广告-备源', adId: 'S52891', platform: 'fengchuan', sourceId: 'test-fc-src-002', refCount: 1, appKeys: ['app_001', 'app_001__iOS-中国大陆', 'app_001__iOS-海外', 'app_001__Android-中国大陆', 'app_001__Android-全球'] },
        ],
    },
    {
        key: '2', name: 'test-插屏广告位', slotId: 'test-gslot-002', type: 'interstitial', createTime: '2026-08-02 14:20:00', refCount: 2,
        sources: [
            { key: 's3', title: 'test-插屏广告-主源', adId: 'S61234', platform: 'fengchuan', sourceId: 'test-fc-src-004', refCount: 2, appKeys: ['app_001', 'app_001__iOS-中国大陆', 'app_001__Android-中国大陆', 'app_002', 'app_002__iOS-中国大陆', 'app_002__Android-全球'] },
        ],
    },
    {
        key: '3', name: 'test-Banner广告位', slotId: 'test-gslot-003', type: 'banner', createTime: '2026-08-03 09:00:00', refCount: 2,
        sources: [
            { key: 's4', title: 'test-Banner广告-主源', adId: 'S32891', platform: 'fengchuan', sourceId: 'test-fc-src-003', refCount: 2, appKeys: [...ALL_APP_KEYS] },
            { key: 's5', title: 'test-Banner广告-备源', adId: 'S33892', platform: 'gdt', sourceId: 'test-gdt-src-001', refCount: 0, appKeys: ['app_003', 'app_003__iOS-海外', 'app_003__Android-海外'] },
        ],
    },
    {
        key: '4', name: 'test-激励视频广告位', slotId: 'test-gslot-004', type: 'rewarded', createTime: '2026-08-04 16:00:00', refCount: 1,
        sources: [
            { key: 's6', title: 'test-激励视频-主源', adId: 'S89012', platform: 'fengchuan', sourceId: 'test-fc-src-006', refCount: 1, appKeys: ['app_002', 'app_002__iOS-中国大陆', 'app_002__Android-全球'] },
        ],
    },
    {
        key: '5', name: 'test-原生广告位', slotId: 'test-gslot-005', type: 'native', createTime: '2026-08-05 08:30:00', refCount: 0,
        sources: [
            { key: 's7', title: 'test-原生广告-主源', adId: 'S34567', platform: 'fengchuan', sourceId: 'test-fc-src-008', refCount: 0, appKeys: ['app_001', 'app_001__iOS-中国大陆', 'app_001__Android-中国大陆', 'app_003', 'app_003__iOS-海外', 'app_003__Android-海外'] },
        ],
    },
    {
        key: '6', name: 'test-开屏广告位（海外）', slotId: 'test-gslot-006', type: 'splash', createTime: '2026-08-06 11:00:00', refCount: 0,
        sources: [],
    },
];

// ══════════════════════════════════════════════
//  子组件：添加/编辑全局广告位抽屉
// ══════════════════════════════════════════════
const SlotFormDrawer = ({ open, editingRecord, onCancel, onSubmit, allSlots }) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        if (open) {
            if (editingRecord) {
                form.setFieldsValue({ name: editingRecord.name, slotId: editingRecord.slotId, type: editingRecord.type });
            } else {
                form.resetFields();
            }
        }
    }, [open, editingRecord, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // 全局唯一校验：编辑时排除自身
            const dup = allSlots.some((s) => s.slotId === values.slotId && (!editingRecord || s.key !== editingRecord.key));
            if (dup) {
                message.error('该广告位ID已存在');
                return;
            }
            onSubmit(values);
        } catch {
            // 校验失败，保持打开
        }
    };

    return (
        <Drawer
            title={editingRecord ? '编辑广告位' : '添加广告位'}
            open={open}
            width={480}
            onClose={onCancel}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={handleSubmit}>保存</Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item name="name" label="广告位名称" rules={[{ required: true, message: '请输入广告位名称' }]}>
                    <Input placeholder="请输入广告位名称" maxLength={30} />
                </Form.Item>
                <Form.Item
                    name="slotId"
                    label="广告位ID"
                    extra="全局唯一标识，创建后不可修改"
                    rules={[{ required: true, message: '请输入广告位ID' }]}
                >
                    <Input placeholder="请输入广告位ID，如 GSLOT_001" disabled={!!editingRecord} />
                </Form.Item>
                <Form.Item name="type" label="广告类型" rules={[{ required: true, message: '请选择广告类型' }]}>
                    <Select placeholder="请选择广告类型" options={AD_TYPE_OPTIONS} />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

// ══════════════════════════════════════════════
//  子组件：广告源配置视图（PAGE-2）
// ══════════════════════════════════════════════
const SourceListView = ({ slot, onBack, onSourcesChange }) => {
    const [sources, setSources] = useState(slot.sources);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingSource, setEditingSource] = useState(null);

    const handleAdd = () => { setEditingSource(null); setDrawerOpen(true); };
    const handleEdit = (record) => { setEditingSource(record); setDrawerOpen(true); };

    const handleSubmit = (values) => {
        if (editingSource) {
            setSources((prev) => prev.map((s) => (s.key === editingSource.key ? { ...s, ...values, adId: editingSource.adId } : s)));
            message.success('编辑成功');
        } else {
            const newSource = { key: Date.now().toString(), adId: 'S' + String(Date.now()).slice(-5), refCount: 0, ...values };
            setSources((prev) => [...prev, newSource]);
            message.success('广告源创建成功');
        }
        setDrawerOpen(false);
    };

    const handleDelete = (record) => {
        if (record.refCount > 0) {
            Modal.confirm({
                title: '无法删除该广告源',
                icon: <ExclamationCircleFilled />,
                content: `该广告源已被 ${record.refCount} 个广告策略引用，需先解绑策略。`,
                okText: '知道了', okType: 'primary', cancelButtonProps: { style: { display: 'none' } },
            });
            return;
        }
        Modal.confirm({
            title: '确定删除该广告源吗？',
            icon: <ExclamationCircleFilled />,
            content: '删除后不可恢复。',
            okText: '确定删除', okType: 'danger', cancelText: '取消',
            onOk: () => {
                setSources((prev) => prev.filter((s) => s.key !== record.key));
                message.success('已删除');
            },
        });
    };

    // 变更后同步回父级
    React.useEffect(() => {
        if (onSourcesChange) onSourcesChange(sources);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sources]);

    const columns = [
        { title: '广告标题', dataIndex: 'title', render: (t) => <Text strong>{t}</Text> },
        { title: '广告ID', dataIndex: 'adId', render: (t) => <Text copyable code>{t}</Text> },
        { title: '广告平台', dataIndex: 'platform', render: (t) => <Tag color="geekblue" style={{ border: 'none' }}>{t}</Tag> },
        { title: '广告源ID', dataIndex: 'sourceId', render: (t) => <Text code>{t}</Text> },
        {
            title: '关联App', dataIndex: 'appKeys', width: 300,
            render: (appKeys) => {
                if (!appKeys || appKeys.length === 0) return <Text type="secondary">—</Text>;
                // 展示格式：App名称-系统平台-区域（二级节点）；统计维度 应用（一级 key）
                const leafKeys = appKeys.filter((k) => k.includes('__'));
                const appNames = appKeys
                    .filter((k) => !k.includes('__'))
                    .map((k) => getAppKeyTitle(k).replace(/\s*（[^）]*）$/, ''));
                const items = leafKeys.length > 0
                    ? leafKeys.map((k) => {
                        const appId = k.split('__')[0];
                        const appTitle = getAppKeyTitle(appId).replace(/\s*（[^）]*）$/, '');
                        return `${appTitle}-${getAppKeyTitle(k)}`;
                    })
                    : appNames;
                const SHOW = 3;
                const shown = items.slice(0, SHOW);
                const hasMore = items.length > SHOW;
                return (
                    <Space size={4} wrap>
                        {shown.map((item, i) => <Tag key={i} color="blue" style={{ border: 'none' }}>{item}</Tag>)}
                        {hasMore && (
                            <Tooltip title={items.join('、')}>
                                <Tag color="blue" style={{ border: 'none', cursor: 'pointer' }}>…</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '操作', dataIndex: 'key', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0, marginBottom: 12 }} onClick={onBack}>返回广告位列表</Button>
            <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <Space size="large">
                    <span>广告位：<Text strong>{slot.name}</Text></span>
                    <span>ID：<Text code>{slot.slotId}</Text></span>
                    <span>类型：<Tag color={AD_TYPE_MAP[slot.type]?.color} style={{ border: 'none' }}>{AD_TYPE_MAP[slot.type]?.label}</Tag></span>
                </Space>
            </div>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">共 {sources.length} 条记录</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加广告源</Button>
                </div>
                <Table columns={columns} dataSource={sources} rowKey="key" bordered={false}
                    pagination={sources.length > 10 ? { showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` } : false}
                    locale={{ emptyText: '暂无广告源，点击上方按钮添加' }}
                />
            </Card>

            <SourceFormDrawer open={drawerOpen} editingRecord={editingSource} onCancel={() => setDrawerOpen(false)} onSubmit={handleSubmit} />
        </div>
    );
};

// ══════════════════════════════════════════════
//  子组件：添加/编辑广告源抽屉
// ══════════════════════════════════════════════
const SourceFormDrawer = ({ open, editingRecord, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [checkedKeys, setCheckedKeys] = useState([]);

    React.useEffect(() => {
        if (open) {
            if (editingRecord) {
                form.setFieldsValue({ title: editingRecord.title, platform: editingRecord.platform, sourceId: editingRecord.sourceId });
                // 回显快照；旧数据无 appKeys 时按默认全选处理
                setCheckedKeys(editingRecord.appKeys || [...ALL_APP_KEYS]);
            } else {
                form.resetFields();
                // 新建默认全选应用（快照语义）
                setCheckedKeys([...ALL_APP_KEYS]);
            }
        }
    }, [open, editingRecord, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (checkedKeys.length === 0) { message.error('请至少选择一个应用'); return; }
            onSubmit({ ...values, appKeys: checkedKeys });
        } catch {
            // 校验失败，保持打开
        }
    };

    return (
        <Drawer
            title={editingRecord ? '编辑广告源' : '添加广告源'}
            open={open}
            width={520}
            onClose={onCancel}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={handleSubmit}>保存</Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item name="title" label="广告标题" rules={[{ required: true, message: '请输入广告标题' }]}>
                    <Input placeholder="请输入广告标题" maxLength={30} />
                </Form.Item>
                <Form.Item name="platform" label="广告平台" rules={[{ required: true, message: '请选择广告平台' }]}>
                    <Select placeholder="请选择广告平台" options={PLATFORM_OPTIONS} />
                </Form.Item>
                <Form.Item name="sourceId" label="广告源ID" rules={[{ required: true, message: '请输入广告源ID' }]}>
                    <Input placeholder="请输入广告源ID" />
                </Form.Item>

                {/* 关联App：两级复选（一级 应用，二级 系统平台-区域），限定投放范围 */}
                <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text strong>关联App <Text type="danger" style={{ fontWeight: 400 }}>*</Text></Text>
                        <Space size={4}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setCheckedKeys([...ALL_APP_KEYS])}>全选</Button>
                            <Text type="secondary">/</Text>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setCheckedKeys([])}>清空</Button>
                        </Space>
                    </div>
                    <Tree
                        checkable
                        selectable={false}
                        defaultExpandAll
                        treeData={MOCK_APP_TREE}
                        checkedKeys={checkedKeys}
                        onCheck={(keys) => setCheckedKeys(keys)}
                    />
                    <Alert
                        type="info" showIcon style={{ marginTop: 8, borderRadius: 6 }} banner
                        message="该广告源仅在勾选的应用对应「系统平台-区域」上生效；保存后固定，新增应用不自动加入"
                    />
                </div>

                {!editingRecord && (
                    <Alert type="info" showIcon message="保存后系统将自动生成广告ID（S + 5位数字）" style={{ borderRadius: 8 }} />
                )}
            </Form>
        </Drawer>
    );
};

// ══════════════════════════════════════════════
//  主组件：全局广告配置管理（PAGE-1）
// ══════════════════════════════════════════════
const GlobalAdSlot = () => {
    const [slots, setSlots] = useState(MOCK_GLOBAL_SLOTS);
    const [keyword, setKeyword] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingSlot, setEditingSlot] = useState(null);
    const [currentView, setCurrentView] = useState('list'); // list | source
    const [currentSlot, setCurrentSlot] = useState(null);

    const handleAdd = () => { setEditingSlot(null); setDrawerOpen(true); };
    const handleEdit = (record) => { setEditingSlot(record); setDrawerOpen(true); };

    const handleSubmit = (values) => {
        if (editingSlot) {
            setSlots((prev) => prev.map((s) => (s.key === editingSlot.key ? { ...s, ...values } : s)));
            message.success('编辑成功');
        } else {
            const newSlot = { key: Date.now().toString(), createTime: new Date().toISOString().slice(0, 19).replace('T', ' '), refCount: 0, sources: [], ...values };
            setSlots((prev) => [...prev, newSlot]);
            message.success('广告位创建成功');
        }
        setDrawerOpen(false);
    };

    const handleDelete = (record) => {
        if (record.refCount > 0) {
            Modal.confirm({
                title: '无法删除该广告位',
                icon: <ExclamationCircleFilled />,
                content: `该广告位已被 ${record.refCount} 个广告策略引用，需先解绑策略。`,
                okText: '知道了', okType: 'primary', cancelButtonProps: { style: { display: 'none' } },
            });
            return;
        }
        Modal.confirm({
            title: '确定删除该广告位吗？',
            icon: <ExclamationCircleFilled />,
            content: '删除后不可恢复。',
            okText: '确定删除', okType: 'danger', cancelText: '取消',
            onOk: () => { setSlots((prev) => prev.filter((s) => s.key !== record.key)); message.success('已删除'); },
        });
    };

    const handleSourceConfig = (record) => { setCurrentSlot(record); setCurrentView('source'); };

    const handleSourcesChange = (sources) => {
        if (currentSlot) {
            setSlots((prev) => prev.map((s) => (s.key === currentSlot.key ? { ...s, sources } : s)));
        }
    };

    // 搜索：输入 test 可查到，其他输入查不到
    const filtered = keyword.trim()
        ? slots.filter((s) => {
            const kw = keyword.trim();
            const hit = s.name.includes(kw) || s.slotId.includes(kw);
            return hit && kw.includes('test');
        })
        : slots;

    const columns = [
        { title: '广告位名称', dataIndex: 'name', render: (t) => <Text strong>{t}</Text> },
        { title: '广告位ID', dataIndex: 'slotId', render: (t) => <Text copyable code>{t}</Text> },
        {
            title: '广告类型', dataIndex: 'type',
            render: (t) => <Tag color={AD_TYPE_MAP[t]?.color} style={{ border: 'none' }}>{AD_TYPE_MAP[t]?.label}</Tag>,
        },
        {
            title: '广告源', dataIndex: 'sources', width: 90,
            render: (sources, r) => (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleSourceConfig(r)}>
                    {sources.length} 个
                </Button>
            ),
        },
        { title: '创建时间', dataIndex: 'createTime', render: (t) => <Text type="secondary">{t}</Text> },
        {
            title: '操作', dataIndex: 'key', fixed: 'right', width: 200,
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" onClick={() => handleSourceConfig(record)}>广告源配置</Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    // PAGE-2 子视图：广告源配置
    if (currentView === 'source' && currentSlot) {
        return (
            <div style={{ padding: 24 }}>
                <Breadcrumb style={{ marginBottom: 12 }} items={[
                    { title: '运营管理' }, { title: '广告配置管理' },
                    { title: <span>{currentSlot.name} · 广告源配置</span> },
                ]} />
                <SourceListView
                    key={currentSlot.key}
                    slot={slots.find((s) => s.key === currentSlot.key) || currentSlot}
                    onBack={() => setCurrentView('list')}
                    onSourcesChange={handleSourcesChange}
                />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Breadcrumb style={{ marginBottom: 12 }} items={[{ title: '运营管理' }, { title: '广告配置管理' }]} />
            <Title level={4} style={{ marginBottom: 16 }}>广告配置管理</Title>

            <Alert
                type="info" showIcon closable style={{ marginBottom: 16, borderRadius: 8 }}
                message="全局广告位与具体App无关，供《广告投放策略》全局投放使用；历史广告版本配置请在《管理App》应用版本中管理。"
            />

            <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Form layout="inline">
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space.Compact>
                            <Select
                                defaultValue="name"
                                style={{ width: 110 }}
                                options={[{ value: 'name', label: '广告位名称' }, { value: 'slotId', label: '广告位ID' }]}
                            />
                            <Input
                                placeholder="输入 test 查询演示数据"
                                allowClear
                                style={{ width: 240 }}
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary">查询</Button>
                            <Button onClick={() => setKeyword('')}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">共 {filtered.length} 条记录</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加广告位</Button>
                </div>
                <Table columns={columns} dataSource={filtered} rowKey="key" bordered={false}
                    pagination={filtered.length > 10 ? { showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` } : false}
                    locale={{ emptyText: '未查询到相关数据' }}
                />
            </Card>

            <SlotFormDrawer open={drawerOpen} editingRecord={editingSlot} onCancel={() => setDrawerOpen(false)} onSubmit={handleSubmit} allSlots={slots} />
        </div>
    );
};

export default GlobalAdSlot;
